import {
    areArrayEquals,
    assert,
    createNamedEqualityChecker,
    getNonEmptyArrayLastElement,
    objectEntries,
    type ReadonlyNonEmptyArray,
    type ReadonlyRecord,
} from '../../utils'

import { ConnectwareError, ConnectwareErrorType, type LoadableTopicsChunk, type Topic, type TopicsChunks } from '..'
import { isLoadableLoaded } from '../Loadable'

export type TopicTree = Readonly<{
    name: Topic['lastLevel']
    fullPath: Topic['fullPath']
    valid: Topic['valid']

    /**
     * If the current topic can still load children
     */
    hasChildrenToLoad: boolean

    /**
     * Children that are already loaded into memory
     */
    children: readonly TopicTree[]

    selected: boolean
    custom: boolean

    /**
     * If there is a chunk loading under the current node (as its prefix)
     */
    isLoadingSibling: boolean

    /**
     * Errors while loading topics chunks under the current node (as its prefix)
     */
    loadingSiblingError: ConnectwareError | null

    /**
     * The next chunk size to be loaded
     */
    nextPageSize: number | null
}>

class TopicTreeBuilder implements TopicTree {
    private isSelected: TopicTree['selected'] = false

    private isCustom: TopicTree['custom'] = false

    private loading: TopicTree['isLoadingSibling'] = false

    private canLoadChildren: TopicTree['hasChildrenToLoad'] = false

    private siblingError: TopicTree['loadingSiblingError'] = null

    private nextSiblingChunkSize: TopicTree['nextPageSize'] = null

    private childNodes: ReadonlyRecord<Topic<false>['fullPath'], TopicTreeBuilder> = {}

    constructor (private readonly topic: Pick<Topic, 'lastLevel' | 'levels' | 'fullPath' | 'valid' | 'createChild'>) {}

    get name (): TopicTree['name'] {
        return this.topic.lastLevel
    }

    get valid (): TopicTree['valid'] {
        return this.topic.valid
    }

    get fullPath (): TopicTree['fullPath'] {
        return this.topic.fullPath
    }

    get selected (): TopicTree['selected'] {
        return this.isSelected
    }

    get custom (): TopicTree['custom'] {
        return this.isCustom
    }

    get isLoadingSibling (): TopicTree['isLoadingSibling'] {
        return this.loading
    }

    get loadingSiblingError (): TopicTree['loadingSiblingError'] {
        return this.siblingError
    }

    get nextPageSize (): TopicTree['nextPageSize'] {
        return this.nextSiblingChunkSize
    }

    get children (): TopicTree['children'] {
        return [...objectEntries(this.childNodes)].sort(([a], [b]) => (a > b ? 1 : -1)).map(([, value]) => value)
    }

    get hasChildrenToLoad (): TopicTree['hasChildrenToLoad'] {
        return this.canLoadChildren
    }

    /**
     * Recursively generates topics from the current builder tree
     * @param childPath the path below the current topic
     *  IE, if you are at "foo", and want to create the path to "foo/bar", then just pass "bar"
     */
    private ensureTopic (childPath: TopicTree['fullPath']): TopicTreeBuilder {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let builder: TopicTreeBuilder = this
        let pathLeft: string | null = childPath

        /** Until there is nowhere else to go */
        while (pathLeft !== null) {
            const [directChildTopic, restOfPath] = builder.topic.createChild(pathLeft)

            assert(
                directChildTopic.lastLevel !== null,
                new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'All child topic should have a path by definition')
            )

            /** Ensure the builder is created within the current builder */
            const builderChild = builder.childNodes[directChildTopic.lastLevel] ?? new TopicTreeBuilder(directChildTopic)
            builder.childNodes = { ...builder.childNodes, [directChildTopic.lastLevel]: builderChild }

            builder = builderChild
            pathLeft = restOfPath
        }

        return builder
    }

    addSelectedTopic (topic: string): void {
        const selectedTopic = this.ensureTopic(topic)
        selectedTopic.isSelected = true
    }

    addCustomTopic (topic: string): void {
        const customTopic = this.ensureTopic(topic)
        customTopic.isCustom = true
    }

    addChunks (prefix: TopicTree['fullPath'], chunks: ReadonlyNonEmptyArray<LoadableTopicsChunk>): void {
        const prefixTopic = this.ensureTopic(prefix)

        for (const chunk of chunks) {
            if (isLoadableLoaded(chunk)) {
                for (const { name, hasChildren } of chunk.topics) {
                    const topic = prefixTopic.ensureTopic(name)
                    topic.canLoadChildren = topic.canLoadChildren || hasChildren
                }
            } else {
                if (chunk === null) {
                    assert(!prefixTopic.loading, new ConnectwareError(ConnectwareErrorType.STATE, 'There is already a loading chunk', { prefix }))
                    prefixTopic.loading = true
                } else {
                    assert(!prefixTopic.siblingError, new ConnectwareError(ConnectwareErrorType.STATE, 'There is already a load chunk error', { prefix }))
                    prefixTopic.siblingError = chunk
                }
            }
        }

        const lastChunk = getNonEmptyArrayLastElement(chunks)
        if (isLoadableLoaded(lastChunk) && lastChunk.nextChunk !== null) {
            prefixTopic.nextSiblingChunkSize = lastChunk.nextChunk.pageSize
        }
    }
}

export const findTreeByTopic = (tree: TopicTree, topic: string): TopicTree | null => {
    if (tree.fullPath === topic) {
        return tree
    }

    /**
     * If this is the root, or the topic starts with this node's path, then check its children
     * Otherwise just yield a null
     */
    return ((tree.fullPath === null || topic.startsWith(tree.fullPath)) && tree.children.find((c) => findTreeByTopic(c, topic))) || null
}

export const buildTopicTree = (
    ...[rootTopic, selected, custom, allChunks]: [
        ...rootTopic: ConstructorParameters<typeof TopicTreeBuilder>,
        selected: readonly string[],
        custom: readonly string[],
        allChunks: TopicsChunks
    ]
): TopicTree => {
    const tree = new TopicTreeBuilder(rootTopic)

    selected.forEach((t) => tree.addSelectedTopic(t))
    custom.forEach((t) => tree.addCustomTopic(t))
    allChunks.forEach((chunks, prefix) => tree.addChunks(prefix, chunks))

    return tree
}

export const areTopicTreesEquals: (a: TopicTree, b: TopicTree) => boolean = createNamedEqualityChecker<TopicTree>(
    {
        name: null,
        fullPath: null,
        valid: null,
        hasChildrenToLoad: null,
        selected: null,
        custom: null,
        isLoadingSibling: null,
        loadingSiblingError: null,
        nextPageSize: null,
        children: (a, b) => areArrayEquals(a, b, { sort: false, equals: areTopicTreesEquals }),
    },
    'areTopicTreesEquals'
)
